/**
 * StepStatsRow (Sprint 4.3.5).
 *
 * Bottom-of-card 3-cell stats row for a Step node on the Pathway Studio
 * canvas. Mirrors the Awell Studio reference screenshot's "actions /
 * stakeholders / locations" footer:
 *   - check  icon + count of child Action nodes
 *   - people icon + count of distinct stakeholders referenced by actions
 *   - pin    icon + count of distinct location references (often 0)
 *
 * Anchored to the canonical Scrips prototype:
 *   https://ai-native-prototype.vercel.app/care-pathway-studio.html
 *   (no canvas surface there; Awell screenshot is the canvas visual SoT).
 *
 * HC2 token-only. HC5 zero em / en dashes. HC6 a11y: each cell carries
 * a discrete `aria-label` ("3 actions", "2 stakeholders", "1 location")
 * so screen-reader users do not need to interpret the icon. Brand-Patch
 * v2: zero TLP1-4 strings.
 */

export interface StepStatsRowProps {
  /** Count of actions (child Action nodes) embedded in the step. */
  actions: number;
  /** Count of distinct stakeholders referenced by actions in this step. */
  stakeholders: number;
  /** Count of distinct location refs. May be 0 for most fixtures. */
  locations: number;
  /** Anchor id to disambiguate test-ids when many rows co-exist. */
  anchorId?: string;
  className?: string;
}

/** Inline SVG check icon. Token-only `currentColor` fill. */
function CheckIcon() {
  return (
    <svg
      data-testid="step-stats-icon-actions"
      width="12"
      height="12"
      viewBox="0 0 24 24"
      role="img"
      aria-hidden="true"
      className="h-3 w-3 text-text-tertiary"
    >
      <path
        d="M5 12.5l4.2 4.2L19 7"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** Inline SVG people / stakeholder icon. */
function PeopleIcon() {
  return (
    <svg
      data-testid="step-stats-icon-stakeholders"
      width="12"
      height="12"
      viewBox="0 0 24 24"
      role="img"
      aria-hidden="true"
      className="h-3 w-3 text-text-tertiary"
    >
      <circle cx="9" cy="8" r="3" fill="currentColor" />
      <circle cx="17" cy="9" r="2.4" fill="currentColor" />
      <path
        d="M3 19c0-3 2.7-5 6-5s6 2 6 5"
        fill="currentColor"
      />
      <path
        d="M14 19c0-2.2 1.6-3.6 3.5-3.6S21 16.8 21 19"
        fill="currentColor"
      />
    </svg>
  );
}

/** Inline SVG location pin icon. */
function PinIcon() {
  return (
    <svg
      data-testid="step-stats-icon-locations"
      width="12"
      height="12"
      viewBox="0 0 24 24"
      role="img"
      aria-hidden="true"
      className="h-3 w-3 text-text-tertiary"
    >
      <path
        d="M12 2c-3.9 0-7 3-7 6.8 0 5.2 7 13.2 7 13.2s7-8 7-13.2C19 5 15.9 2 12 2zm0 9.4a2.6 2.6 0 110-5.2 2.6 2.6 0 010 5.2z"
        fill="currentColor"
      />
    </svg>
  );
}

interface CellProps {
  icon: React.ReactNode;
  count: number;
  label: string;
  testId: string;
}

function Cell({ icon, count, label, testId }: CellProps) {
  return (
    <span
      data-testid={testId}
      aria-label={`${count} ${label}`}
      className="flex items-center gap-1"
    >
      {icon}
      <span className="text-signal-xs tabular-nums text-text-secondary">
        {count}
      </span>
    </span>
  );
}

export function StepStatsRow({
  actions,
  stakeholders,
  locations,
  anchorId,
  className,
}: StepStatsRowProps) {
  const testId = `step-stats-row-${anchorId ?? 'default'}`;
  return (
    <div
      data-testid={testId}
      role="group"
      aria-label="Step stats"
      className={[
        'flex items-center gap-gutter-md',
        className ?? '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <Cell
        icon={<CheckIcon />}
        count={actions}
        label={actions === 1 ? 'action' : 'actions'}
        testId={`${testId}-actions`}
      />
      <Cell
        icon={<PeopleIcon />}
        count={stakeholders}
        label={stakeholders === 1 ? 'stakeholder' : 'stakeholders'}
        testId={`${testId}-stakeholders`}
      />
      <Cell
        icon={<PinIcon />}
        count={locations}
        label={locations === 1 ? 'location' : 'locations'}
        testId={`${testId}-locations`}
      />
    </div>
  );
}
